import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { getLLAuthHeaders } from '../../utils/llAuthHeaders';
import {
  deleteRole as withoutRole,
  loadSetup,
  mergeLoadedRoles,
  roleUpdate,
  saveSetup,
  settleLocally,
  type SetupRole,
  type SetupTeam
} from './capabilitySetup';

/**
 * The organization's Capability Setup: its teams, their roles, and how changes
 * to the roles are kept.
 *
 * `loading`: the first read is on its way.
 *
 * `api`: read from and written to the backend (GET / POST / PUT / DELETE
 * under /v3/admin/capability-setup). The backend applies the same rules as
 * capabilitySetup.ts and its answer is the truth. A role's saves go one at a
 * time, each sent with the version the one before it was answered with, so a
 * slow answer can never undo a newer save.
 *
 * `local`: DUMMY, development only. When the API has no Capability Setup
 * endpoints (a local front end pointed at an API that predates them, so the GET
 * answers 404), the setup is kept in this browser, so the page can still be
 * worked on. Remove once the endpoints are deployed everywhere.
 *
 * `error`: the read failed; `loadError` says how.
 */
export type RolesMode = 'loading' | 'api' | 'local' | 'error';

export interface LoadError {
  /** The HTTP status, or null when no answer came back (offline, timed out). */
  status: number | null;
  message: string;
  /** A 404 from an API without the Capability Setup endpoints, not one the backend answered itself. */
  missingEndpoints: boolean;
}

/** Adding a role: the role as the backend created it, or why it was refused. */
export type CreateResult = { ok: true; role: SetupRole } | { ok: false; message: string };

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || fallback;

/**
 * The backend answers its own errors as `{ message, code }` (a role or an
 * organization not found is a 404 too); a route the API doesn't have comes back
 * without it.
 */
const isMissingEndpoint = (err: any) => err?.response?.status === 404 && err.response.data?.code !== 404;

/** A role as the backend returns it, in the page's shape. */
const fromApi = (r: any): SetupRole => ({
  id: String(r.id),
  teamId: String(r.teamId),
  name: String(r.name),
  capabilities: Array.isArray(r.capabilities) ? r.capabilities : [],
  capabilitiesConfirmed: Boolean(r.capabilitiesConfirmed),
  levelsConfirmed: Boolean(r.levelsConfirmed),
  established: Boolean(r.established),
  launched: Boolean(r.launched),
  // The capabilities of its own that count while its levels are reopened (absent from a backend before it kept them).
  confirmedCapabilityIds: Array.isArray(r.confirmedCapabilityIds) ? r.confirmedCapabilityIds.map(String) : [],
  // Ready to assess, train on and invite to, as the backend counts it (the shared capabilities applied included).
  ...(typeof r.ready === 'boolean' ? { ready: r.ready } : {}),
  version: Number(r.version) || 0
});

/** A team as the backend returns it. */
const teamFromApi = (t: any): SetupTeam => ({
  id: String(t.id),
  name: String(t.name),
  canEdit: Boolean(t.canEdit),
  managers: (Array.isArray(t.managers) ? t.managers : []).map((m: any) => ({ id: String(m.id), name: String(m.name) })),
  invited: Boolean(t.invitedAt)
});

export const useCapabilityRoles = (orgId: string, isPlatformAdmin: boolean) => {
  const [roles, setRoles] = useState<SetupRole[]>([]);
  const [teams, setTeams] = useState<SetupTeam[]>([]);
  const [teamsList, setTeamsList] = useState<{ listId: string; listName: string } | null>(null);
  const [mode, setMode] = useState<RolesMode>('loading');
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  // How many saves (a delete counts) each role has queued or on their way, by role id.
  const [pending, setPending] = useState<Record<string, number>>({});

  // The roles as last answered, ahead of the render: a queued save reads its version here when it goes.
  const latest = useRef<SetupRole[]>([]);
  // Per role, the last save queued: the next one waits for it.
  const queues = useRef(new Map<string, Promise<void>>());
  // Which read is the latest; an earlier one answering late is dropped.
  const loadSeq = useRef(0);
  // A read the page waits on (the first, or a Retry) is on its way.
  const reading = useRef(false);
  // The org the page is showing, as `${orgId}|${forOrg}`.
  const shownOrg = useRef('');
  // Roles deleted here, and roles added here since the latest read went out (see mergeLoadedRoles).
  const deleted = useRef(new Set<string>());
  const added = useRef(new Set<string>());
  const alive = useRef(true);
  // Platform admins name the org; everyone else's token carries it.
  const forOrg = isPlatformAdmin ? orgId : undefined;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const show = useCallback((next: SetupRole[]) => {
    latest.current = next;
    setRoles(next);
  }, []);

  /**
   * Reads the setup. In the background (after a refused save) the page keeps
   * showing what it has if the read fails, and a read that answers keeps what
   * was saved here while it was on its way. A background read is only for the
   * org on screen, and is skipped while the page waits on a read of its own.
   */
  const load = useCallback(
    async (background = false) => {
      const org = `${orgId}|${forOrg ?? ''}`;
      if (background && (reading.current || org !== shownOrg.current)) return;
      loadSeq.current += 1;
      const seq = loadSeq.current;
      added.current = new Set();
      if (!background) {
        reading.current = true;
        shownOrg.current = org;
        setMode('loading');
        setLoadError(null);
      }
      try {
        const res: any = await API.getCapabilitySetup({ ...getLLAuthHeaders(), orgId: forOrg });
        if (!alive.current || seq !== loadSeq.current) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const loaded: SetupRole[] = (Array.isArray(data.roles) ? data.roles : []).map(fromApi);
        show(
          background
            ? mergeLoadedRoles(latest.current, loaded, { deleted: deleted.current, added: added.current })
            : loaded
        );
        setTeams((Array.isArray(data.teams) ? data.teams : []).map(teamFromApi));
        setTeamsList(
          data.teamsList?.listId
            ? { listId: String(data.teamsList.listId), listName: String(data.teamsList.listName ?? '') }
            : null
        );
        setMode('api');
      } catch (err: any) {
        if (!alive.current || seq !== loadSeq.current || background) return;
        const missingEndpoints = isMissingEndpoint(err);
        if (missingEndpoints && process.env.NODE_ENV === 'development') {
          // DUMMY: this API has no Capability Setup endpoints yet; keep the setup in the browser.
          show(loadSetup(orgId));
          setTeams([]);
          setTeamsList(null);
          setMode('local');
          return;
        }
        show([]);
        setLoadError({
          status: err?.response?.status ?? null,
          message: errorMessage(err, 'Something went wrong. Check your connection and try again.'),
          missingEndpoints
        });
        setMode('error');
      } finally {
        if (!background && seq === loadSeq.current) reading.current = false;
      }
    },
    [orgId, forOrg, show]
  );

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(() => load(), [load]);
  /** Reads the setup again without leaving the page (no loading state): roles merge with what is in flight. */
  const refresh = useCallback(() => load(true), [load]);

  const track = useCallback(
    (roleId: string, step: number) =>
      setPending((current) => {
        const count = (current[roleId] ?? 0) + step;
        const { [roleId]: _counted, ...rest } = current;
        return count > 0 ? { ...rest, [roleId]: count } : rest;
      }),
    []
  );

  /** Runs `work` once everything queued for the role before it has finished. */
  const enqueue = useCallback(
    (roleId: string, work: () => Promise<boolean>): Promise<boolean> => {
      const before = queues.current.get(roleId) ?? Promise.resolve();
      const run = before.then(work);
      const done = run.then(
        () => undefined,
        () => undefined
      );
      queues.current.set(roleId, done);
      track(roleId, 1);
      done.then(() => {
        track(roleId, -1);
        if (queues.current.get(roleId) === done) queues.current.delete(roleId);
      });
      return run;
    },
    [track]
  );

  /** A role as a save answered it (ids included), unless a newer answer is already in. */
  const adopt = useCallback(
    (answer: SetupRole) => {
      const { current } = latest;
      const here = current.find((r) => r.id === answer.id);
      if (!here || (here.version ?? 0) > (answer.version ?? 0)) return;
      show(current.map((r) => (r.id === answer.id ? answer : r)));
    },
    [show]
  );

  /**
   * Saves a change to a role. `change` turns the role as last saved into the
   * role as it should be, when the save goes (after any queued before it), and
   * only what differs is sent, with the version it was made from. Resolves true
   * once saved (or with nothing to save); false when refused: the reason is
   * shown and the saved setup is read again in the background.
   */
  const saveRole = useCallback(
    (roleId: string, change: (role: SetupRole) => SetupRole): Promise<boolean> => {
      if (mode === 'local') {
        // DUMMY: kept as the backend would keep it (confirmed once, for good).
        const next = latest.current.map((r) => (r.id === roleId ? settleLocally(r, change(r)) : r));
        show(next);
        saveSetup(orgId, next);
        return Promise.resolve(true);
      }
      return enqueue(roleId, async () => {
        const saved = latest.current.find((r) => r.id === roleId);
        if (!saved) return false;
        const update = roleUpdate(saved, change(saved));
        if (!update) return true;
        try {
          const res: any = await API.updateCapabilityRole({
            ...getLLAuthHeaders(),
            orgId: forOrg,
            roleId,
            role: { version: saved.version ?? 0, ...update }
          });
          adopt(fromApi(res?.data?.data ?? res?.data));
          return true;
        } catch (err) {
          toast.error(errorMessage(err, 'That change could not be saved. Check your connection and try again.'));
          load(true);
          return false;
        }
      });
    },
    [mode, orgId, forOrg, show, enqueue, adopt, load]
  );

  /** Deletes a role, after any save queued for it. Resolves true once it is gone. */
  const deleteRole = useCallback(
    (roleId: string): Promise<boolean> => {
      if (mode === 'local') {
        const next = withoutRole(latest.current, roleId);
        show(next);
        saveSetup(orgId, next);
        return Promise.resolve(true);
      }
      return enqueue(roleId, async () => {
        try {
          await API.deleteCapabilityRole({ ...getLLAuthHeaders(), orgId: forOrg, roleId });
          deleted.current.add(roleId);
          show(latest.current.filter((r) => r.id !== roleId));
          return true;
        } catch (err) {
          toast.error(errorMessage(err, 'That role could not be deleted. Check your connection and try again.'));
          load(true);
          return false;
        }
      });
    },
    [mode, orgId, forOrg, show, enqueue, load]
  );

  /**
   * Adds a role to a team. In `api` mode the backend creates it, and may refuse
   * it (a name the team already has, a team the viewer doesn't manage); in
   * `local` mode `fallback` is the role to add.
   */
  const createRole = useCallback(
    async (teamId: string, name: string, fallback: SetupRole): Promise<CreateResult> => {
      if (mode === 'local') {
        const next = [...latest.current, fallback];
        show(next);
        saveSetup(orgId, next);
        return { ok: true, role: fallback };
      }
      const org = `${orgId}|${forOrg ?? ''}`;
      try {
        const res: any = await API.createCapabilityRole({ ...getLLAuthHeaders(), orgId: forOrg, teamId, name });
        const created = fromApi(res?.data?.data ?? res?.data);
        if (org === shownOrg.current) {
          added.current.add(created.id);
          show([...latest.current.filter((r) => r.id !== created.id), created]);
        }
        return { ok: true, role: created };
      } catch (err: any) {
        // Someone else's role by that name, or a team that has gone: read again to show it.
        if (err?.response?.status === 409 || err?.response?.status === 404) load(true);
        return {
          ok: false,
          message: errorMessage(err, 'That role could not be added. Check your connection and try again.')
        };
      }
    },
    [mode, orgId, forOrg, show, load]
  );

  /** A save (or the delete) of this role is queued or on its way. */
  const isSaving = useCallback((roleId: string) => (pending[roleId] ?? 0) > 0, [pending]);

  return { roles, teams, teamsList, mode, loadError, reload, refresh, createRole, saveRole, deleteRole, isSaving };
};
